import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ArrowDownCircle, ArrowUpCircle, ArrowLeftRight, ChevronDown, ChevronRight, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { API } from "@/lib/api";
import useSort from "@/lib/useSort";

export default function LogMagazzino() {
  const [movs, setMovs] = useState([]);
  const [products, setProducts] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [m, p, d] = await Promise.all([
        api.get("/movements?limit=1000"),
        api.get("/products"),
        api.get("/deposits"),
      ]);
      setMovs(m.data);
      setProducts(p.data);
      setDeposits(d.data);
    } catch (e) { toast.error("Errore caricamento log"); }
  };
  useEffect(() => { load(); }, []);

  const productName = (id) => products.find((p) => p.id === id)?.nome_articolo || "—";
  const depositName = (id) => deposits.find((d) => d.id === id)?.nome || "—";

  // Group movements:
  // 1) trasferimenti -> raggruppati per transfer_id (uno scarico + uno carico) e poi
  //    super-raggruppati per (from_deposito, to_deposito, stesso giorno, stesso utente)
  // 2) scarichi/carichi multipli stesso magazzino + stesso tipo + stesso minuto + stesso utente -> bulk row
  // 3) singoli -> riga singola
  const grouped = useMemo(() => {
    const transferMap = new Map();
    const bulkMap = new Map();
    const singles = [];
    movs.forEach((m) => {
      if (m.transfer_id) {
        if (!transferMap.has(m.transfer_id)) transferMap.set(m.transfer_id, []);
        transferMap.get(m.transfer_id).push(m);
        return;
      }
      // bulk key: tipo|deposito|utente|minute
      const minute = (m.created_at || "").slice(0, 16); // YYYY-MM-DDTHH:MM
      const bulkKey = `${m.tipo}|${m.deposito_id || ""}|${m.utente_id || m.utente_nome || ""}|${minute}`;
      if (!bulkMap.has(bulkKey)) bulkMap.set(bulkKey, []);
      bulkMap.get(bulkKey).push(m);
    });

    // Step 1: build single transfer groups (one transfer_id = one scarico + one carico)
    const singleTransfers = Array.from(transferMap.entries()).map(([key, list]) => {
      const primary = list.find((x) => x.tipo === "scarico") || list[0];
      const fromId = list.find((x) => x.tipo === "scarico")?.deposito_id
        || list.find((x) => x.from_deposito_id)?.from_deposito_id
        || null;
      const toId = list.find((x) => x.tipo === "carico")?.deposito_id
        || list.find((x) => x.to_deposito_id)?.to_deposito_id
        || null;
      const day = (primary?.created_at || "").slice(0, 10); // YYYY-MM-DD
      const userId = primary?.utente_id || primary?.utente_nome || "";
      return {
        key,
        type: "transfer",
        transfer_id: key,
        primary,
        all: list,
        fromId,
        toId,
        day,
        userId,
      };
    });

    // Step 2: super-group transfers that share (from, to, day, user)
    const superMap = new Map();
    singleTransfers.forEach((g) => {
      const k = `${g.fromId || ""}|${g.toId || ""}|${g.day}|${g.userId}`;
      if (!superMap.has(k)) superMap.set(k, []);
      superMap.get(k).push(g);
    });
    const transferGroups = [];
    superMap.forEach((groupList, k) => {
      if (groupList.length === 1) {
        transferGroups.push(groupList[0]);
      } else {
        // Merge into a transfer-bulk row
        const all = groupList.flatMap((g) => g.all);
        // Use latest as primary for display sorting
        const primary = groupList
          .map((g) => g.primary)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        transferGroups.push({
          key: `tbulk|${k}`,
          type: "transfer-bulk",
          primary,
          all,
          subTransfers: groupList,
          fromId: groupList[0].fromId,
          toId: groupList[0].toId,
          day: groupList[0].day,
          userId: groupList[0].userId,
        });
      }
    });

    const bulkGroups = [];
    bulkMap.forEach((list, key) => {
      if (list.length === 1) {
        const m = list[0];
        singles.push({ key: m.id, type: "single", primary: m, all: [m] });
      } else {
        const primary = list[0];
        bulkGroups.push({ key, type: "bulk", primary, all: list });
      }
    });
    return [...transferGroups, ...bulkGroups, ...singles].sort(
      (a, b) => new Date(b.primary.created_at) - new Date(a.primary.created_at)
    );
  }, [movs]);

  const filtered = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    return grouped.filter((g) =>
      g.all.some((m) =>
        [productName(m.prodotto_id), depositName(m.deposito_id), m.utente_nome, m.note]
          .some((v) => v && String(v).toLowerCase().includes(q))
      )
    );
  }, [grouped, search, products, deposits]);

  const { toggleSort, SortIcon, sortedRows } = useSort(filtered, {
    initial: { by: null, dir: "desc" },
    getKey: (row, by) => {
      const m = row.primary;
      if (by === "data") return m.created_at;
      if (by === "tipo") return row.type === "transfer" ? "trasferimento" : m.tipo;
      if (by === "prodotto") return productName(m.prodotto_id).toLowerCase();
      if (by === "deposito") return depositName(m.deposito_id).toLowerCase();
      if (by === "quantita") return Number(m.quantita) || 0;
      return m[by];
    },
  });

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const downloadPdf = async (movId) => {
    try {
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/movements/${movId}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("PDF non disponibile");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `movimento-${movId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(err.message); }
  };

  const downloadBulkPdf = async (ids) => {
    try {
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/movements/bulk-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error("PDF non disponibile");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `movimenti-bulk-${ids[0].slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(err.message); }
  };

  const TipoIcon = ({ row }) => {
    if (row.type === "transfer" || row.type === "transfer-bulk") return <ArrowLeftRight size={14} style={{ color: "var(--ds-primary)" }} />;
    if (row.primary.tipo === "carico") return <ArrowDownCircle size={14} style={{ color: "#7B8B6F" }} />;
    return <ArrowUpCircle size={14} style={{ color: "#8B3A36" }} />;
  };

  return (
    <div className="space-y-6" data-testid="log-magazzino-page">
      <div>
        <div className="label-tag mb-2">Storico</div>
        <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">Log Magazzino</h1>
        <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
          Tutti i movimenti di carico, scarico e trasferimento. Espandi una riga per vederne il dettaglio.
        </p>
      </div>

      <div className="ds-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca prodotto, deposito, utente, note…" className="rounded-lg max-w-md" data-testid="log-search" />
          <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>{sortedRows.length} operazioni</div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("data")} data-testid="sort-log-data">Data<SortIcon col="data" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("tipo")}>Tipo<SortIcon col="tipo" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("prodotto")}>Prodotto<SortIcon col="prodotto" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("deposito")}>Deposito</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("quantita")}>Q.tà<SortIcon col="quantita" /></TableHead>
                <TableHead>Utente</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun movimento</TableCell></TableRow>}
              {sortedRows.map((row) => {
                const m = row.primary;
                const isOpen = expanded.has(row.key);
                const isTransfer = row.type === "transfer";
                const isTransferBulk = row.type === "transfer-bulk";
                const isAnyTransfer = isTransfer || isTransferBulk;
                const isBulk = row.type === "bulk";
                const fromDep = isAnyTransfer
                  ? (row.fromId || row.all.find((x) => x.tipo === "scarico")?.deposito_id)
                  : null;
                const toDep = isAnyTransfer
                  ? (row.toId || row.all.find((x) => x.tipo === "carico")?.deposito_id)
                  : null;
                // Total quantity: bulk = sum, transfer = sum of scarico (each unit moved once),
                // transfer-bulk = sum of all scarico across sub-transfers
                let totalQta = m.quantita;
                if (isBulk) {
                  totalQta = row.all.reduce((s, x) => s + (Number(x.quantita) || 0), 0);
                } else if (isTransferBulk) {
                  totalQta = row.all
                    .filter((x) => x.tipo === "scarico")
                    .reduce((s, x) => s + (Number(x.quantita) || 0), 0);
                }
                const productLabel = (() => {
                  if (isBulk) return <span style={{ color: "var(--ds-primary)" }}>{row.all.length} prodotti</span>;
                  if (isTransferBulk) {
                    // Distinct products across all sub-transfers
                    const distinct = new Set(row.all.map((x) => x.prodotto_id));
                    return <span style={{ color: "var(--ds-primary)" }}>{distinct.size} prodotti · {row.subTransfers.length} trasferimenti</span>;
                  }
                  return productName(m.prodotto_id);
                })();
                return (
                  <React.Fragment key={row.key}>
                    <TableRow
                      data-testid={`log-row-${row.key}`}
                      className="cursor-pointer hover:bg-[var(--ds-bg)]"
                      onClick={() => toggleExpand(row.key)}
                    >
                      <TableCell>
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </TableCell>
                      <TableCell className="text-sm">
                        {isTransferBulk
                          ? new Date(m.created_at).toLocaleDateString("it-IT")
                          : new Date(m.created_at).toLocaleString("it-IT")}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: isAnyTransfer ? "var(--ds-primary)" : m.tipo === "carico" ? "#7B8B6F" : "#8B3A36" }}>
                          <TipoIcon row={row} />
                          {isAnyTransfer ? (isTransferBulk ? "trasferimenti" : "trasferimento") : m.tipo}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {productLabel}
                      </TableCell>
                      <TableCell className="text-sm">
                        {isAnyTransfer
                          ? `${depositName(fromDep)} → ${depositName(toDep)}`
                          : depositName(m.deposito_id)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {isBulk || isTransferBulk ? `${totalQta} tot.` : m.quantita}
                      </TableCell>
                      <TableCell className="text-xs">{m.utente_nome || "—"}</TableCell>
                      <TableCell className="text-right">
                        {isBulk || isTransferBulk ? (
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); downloadBulkPdf(row.all.map((x) => x.id)); }} title="Scarica PDF unico con tutti i prodotti" data-testid={`log-pdf-bulk-${row.key}`}>
                            <Download size={14} />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); downloadPdf(m.id); }} title="Scarica PDF" data-testid={`log-pdf-${row.key}`}>
                            <Download size={14} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow data-testid={`log-detail-${row.key}`}>
                        <TableCell colSpan={8} className="bg-[var(--ds-bg)]">
                          <div className="p-4 space-y-3">
                            {isTransferBulk ? (
                              <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div>
                                    <div className="label-tag">Operazione</div>
                                    <div className="font-medium mt-1">Trasferimenti multipli ({row.subTransfers.length})</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Da</div>
                                    <div className="font-medium mt-1">{depositName(fromDep)}</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">A</div>
                                    <div className="font-medium mt-1">{depositName(toDep)}</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Utente</div>
                                    <div className="font-medium mt-1">{m.utente_nome || "—"}</div>
                                  </div>
                                </div>
                                <div className="border rounded-lg overflow-hidden mt-3" style={{ borderColor: "var(--ds-border)" }}>
                                  <div className="grid grid-cols-[1.4fr_3fr_1fr_60px] gap-2 px-3 py-2 text-xs uppercase tracking-wider font-medium" style={{ background: "var(--ds-surface)", color: "var(--ds-text-secondary)" }}>
                                    <div>Data/ora</div>
                                    <div>Prodotto</div>
                                    <div className="text-right">Quantità</div>
                                    <div className="text-right">PDF</div>
                                  </div>
                                  {row.subTransfers
                                    .slice()
                                    .sort((a, b) => new Date(b.primary.created_at) - new Date(a.primary.created_at))
                                    .map((sub) => {
                                      const scaricoMov = sub.all.find((x) => x.tipo === "scarico") || sub.primary;
                                      return (
                                        <div key={sub.key} className="grid grid-cols-[1.4fr_3fr_1fr_60px] gap-2 items-center px-3 py-2 border-t text-sm" style={{ borderColor: "var(--ds-border)" }}>
                                          <div className="text-xs">{new Date(scaricoMov.created_at).toLocaleString("it-IT")}</div>
                                          <div className="font-medium">{productName(scaricoMov.prodotto_id)}</div>
                                          <div className="text-right font-medium" style={{ color: "var(--ds-primary)" }}>
                                            {scaricoMov.quantita}
                                          </div>
                                          <div className="text-right">
                                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(scaricoMov.id)} title="Scarica PDF singolo trasferimento">
                                              <Download size={12} />
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                                <div className="flex justify-end">
                                  <Button variant="outline" size="sm" onClick={() => downloadBulkPdf(row.all.map((x) => x.id))} className="rounded-lg" data-testid={`log-pdf-tbulk-detail-${row.key}`}>
                                    <FileText size={14} className="mr-1.5" /> Scarica PDF unico ({row.subTransfers.length} trasferimenti)
                                  </Button>
                                </div>
                              </>
                            ) : isBulk ? (
                              <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div>
                                    <div className="label-tag">Operazione</div>
                                    <div className="font-medium mt-1">{m.tipo} multiplo</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Deposito</div>
                                    <div className="font-medium mt-1">{depositName(m.deposito_id)}</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Utente</div>
                                    <div className="font-medium mt-1">{m.utente_nome || "—"}</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Data/ora</div>
                                    <div className="font-medium mt-1">{new Date(m.created_at).toLocaleString("it-IT")}</div>
                                  </div>
                                </div>
                                <div className="border rounded-lg overflow-hidden mt-3" style={{ borderColor: "var(--ds-border)" }}>
                                  <div className="grid grid-cols-[3fr_1fr_1fr] gap-2 px-3 py-2 text-xs uppercase tracking-wider font-medium" style={{ background: "var(--ds-surface)", color: "var(--ds-text-secondary)" }}>
                                    <div>Prodotto</div>
                                    <div className="text-right">Quantità</div>
                                    <div className="text-right">PDF</div>
                                  </div>
                                  {row.all.map((sub) => (
                                    <div key={sub.id} className="grid grid-cols-[3fr_1fr_1fr] gap-2 items-center px-3 py-2 border-t text-sm" style={{ borderColor: "var(--ds-border)" }}>
                                      <div className="font-medium">{productName(sub.prodotto_id)}</div>
                                      <div className="text-right font-medium" style={{ color: sub.tipo === "carico" ? "#7B8B6F" : "#8B3A36" }}>
                                        {sub.tipo === "carico" ? "+" : "−"}{sub.quantita}
                                      </div>
                                      <div className="text-right">
                                        <Button size="sm" variant="ghost" onClick={() => downloadPdf(sub.id)} title="Scarica PDF singolo">
                                          <Download size={12} />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {m.note && (
                                  <div>
                                    <div className="label-tag">Note</div>
                                    <div className="text-sm mt-1" style={{ color: "var(--ds-text-secondary)" }}>{m.note}</div>
                                  </div>
                                )}
                                <div className="flex justify-end">
                                  <Button variant="outline" size="sm" onClick={() => downloadBulkPdf(row.all.map((x) => x.id))} className="rounded-lg" data-testid={`log-pdf-bulk-detail-${row.key}`}>
                                    <FileText size={14} className="mr-1.5" /> Scarica PDF unico ({row.all.length} prodotti)
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div>
                                    <div className="label-tag">Operazione</div>
                                    <div className="font-medium mt-1">{isTransfer ? "Trasferimento" : m.tipo}</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Prodotto</div>
                                    <div className="font-medium mt-1">{productName(m.prodotto_id)}</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Quantità</div>
                                    <div className="font-medium mt-1">{m.quantita}</div>
                                  </div>
                                  <div>
                                    <div className="label-tag">Data/ora</div>
                                    <div className="font-medium mt-1">{new Date(m.created_at).toLocaleString("it-IT")}</div>
                                  </div>
                                  {isTransfer ? (
                                    <>
                                      <div>
                                        <div className="label-tag">Da</div>
                                        <div className="font-medium mt-1">{depositName(fromDep)}</div>
                                      </div>
                                      <div>
                                        <div className="label-tag">A</div>
                                        <div className="font-medium mt-1">{depositName(toDep)}</div>
                                      </div>
                                    </>
                                  ) : (
                                    <div>
                                      <div className="label-tag">Deposito</div>
                                      <div className="font-medium mt-1">{depositName(m.deposito_id)}</div>
                                    </div>
                                  )}
                                  <div>
                                    <div className="label-tag">Utente</div>
                                    <div className="font-medium mt-1">{m.utente_nome || "—"}</div>
                                  </div>
                                  {m.note && (
                                    <div className="col-span-2 md:col-span-4">
                                      <div className="label-tag">Note</div>
                                      <div className="text-sm mt-1" style={{ color: "var(--ds-text-secondary)" }}>{m.note}</div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex justify-end">
                                  <Button variant="outline" size="sm" onClick={() => downloadPdf(m.id)} className="rounded-lg">
                                    <FileText size={14} className="mr-1.5" /> Scarica PDF dettaglio
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
